/**
 * Data Slice
 *
 * State and actions for spaces, items, and data mappings.
 */

import type { StateCreator } from 'zustand';
import type { Folder, ListItem } from '@/types';
import type { Project } from '@/db/projects';
import type { AppStore, DataSlice } from '../types';
import { logger } from '@/utils/logger';

export const createDataSlice: StateCreator<AppStore, [], [], DataSlice> = (set, get) => ({
  spacesLoading: true,
  spaces: [],
  categories: [],
  foldersMap: {},
  listsMap: {},
  spacesInitialLoadDone: false,
  spacesLoadingInProgress: false,
  projectItems: {},
  folderMappings: {},
  editingItems: new Set<string>(),
  pendingItemCreations: {},
  sidebarStats: { overdue: 0, today: 0, inProgress: 0 },

  setSpacesLoading: (loading) => set({ spacesLoading: loading }),

  setSpaces: (spaces) => set({ spaces }),

  setCategories: (categories) => set({ categories }),

  setFoldersMap: (foldersMap) => {
    const current = get().foldersMap;
    if (current !== foldersMap) {
      const keys = Object.keys(foldersMap);
      if (Object.keys(current).length !== keys.length || !keys.every(k => current[k] === foldersMap[k])) {
        set({ foldersMap });
      }
    }
  },

  setListsMap: (listsMap) => {
    const current = get().listsMap;
    if (current !== listsMap) {
      const keys = Object.keys(listsMap);
      if (Object.keys(current).length !== keys.length || !keys.every(k => current[k] === listsMap[k])) {
        set({ listsMap });
      }
    }
  },

  setSpacesInitialLoadDone: (done) => set({ spacesInitialLoadDone: done }),
  setSpacesLoadingInProgress: (inProgress) => set({ spacesLoadingInProgress: inProgress }),

  setSpaceItems: (items) => set({ projectItems: items }),

  updateSpaceItems: (spaceName, items) => {
    const current = get().projectItems;
    set({ projectItems: { ...current, [spaceName]: items } });
  },

  removeSpaceItems: (spaceName) => {
    const current = get().projectItems;
    const { [spaceName]: _spaceName, ...remaining } = current;
    set({ projectItems: remaining });
  },

  setFolderMappings: (mappings) => set({ folderMappings: mappings }),

  addItemOptimistic: ({ spaceName, itemId, itemName, itemType, parentId, folderId }) => {
    const currentSpaces = get().spaces;
    const currentItems = get().projectItems;

    if (itemType === 'folder') {
      const updatedSpaces = currentSpaces.map((space) => {
        if (space.name !== spaceName) return space;

        const newFolder: Folder = {
          id: itemId,
          name: itemName,
          lists: [],
          taskCount: 0,
        };

        return {
          ...space,
          folders: [...space.folders, newFolder],
        };
      });
      set({ spaces: updatedSpaces });

      const currentMappings = get().folderMappings;
      const spaceMappings = currentMappings[spaceName] || {};
      set({
        folderMappings: {
          ...currentMappings,
          [spaceName]: {
            ...spaceMappings,
            [itemName]: itemId,
          },
        },
      });
    } else if (itemType === 'list') {
      const updatedSpaces = currentSpaces.map((space) => {
        if (space.name !== spaceName) return space;

        const newList: ListItem = {
          id: itemId,
          name: itemName,
          taskCount: 0,
        };

        const updatedFolders = space.folders.map((folder) => {
          if (folderId && (folder.id === folderId || String(folder.id) === String(folderId))) {
            return { ...folder, lists: [...folder.lists, newList] };
          }
          return folder;
        });

        if (!folderId && updatedFolders.length > 0) {
          updatedFolders[0] = { ...updatedFolders[0], lists: [...updatedFolders[0].lists, newList] };
        }

        return { ...space, folders: updatedFolders };
      });
      set({ spaces: updatedSpaces });
    } else {
      const tempItem: Project & { id: string } = {
        id: itemId,
        name: itemName,
        type: itemType,
        content: itemType === 'document' ? '' : itemType === 'chat' || itemType === 'presentation' ? [] : {},
        parent_id: parentId,
        project_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        order_index: null,
      } as Project & { id: string };

      const spaceItems = currentItems[spaceName] || [];
      set({
        projectItems: {
          ...currentItems,
          [spaceName]: [...spaceItems, tempItem],
        },
      });
    }
  },

  updateFolderMappingOptimistic: (spaceName, folderName, folderId) => {
    const current = get().folderMappings;
    const spaceMappings = current[spaceName] || {};
    set({
      folderMappings: {
        ...current,
        [spaceName]: {
          ...spaceMappings,
          [folderName]: folderId,
        },
      },
    });
  },

  setEditingItem: (itemId, editing) => {
    const current = get().editingItems;
    const newSet = new Set(current);
    if (editing) {
      newSet.add(itemId);
    } else {
      newSet.delete(itemId);
    }
    set({ editingItems: newSet });
  },

  setPendingItemCreation: (itemId, info) => {
    const current = get().pendingItemCreations;
    if (info === null) {
      const { [itemId]: _itemId, ...remaining } = current;
      set({ pendingItemCreations: remaining });
    } else {
      set({ pendingItemCreations: { ...current, [itemId]: info } });
    }
  },

  savePendingItemName: async (itemId, name) => {
    logger.log('[DataSlice] savePendingItemName called without implementation', itemId, name);
    get().addToast('Save failed: savePendingItemName not initialized', 'error');
  },

  clearEditingItems: () => set({ editingItems: new Set<string>() }),

  setSidebarStats: (stats) => set({ sidebarStats: stats }),
});
